import { Serializer } from '../resource/serializer.interface';
import {
  NotificationCatalog, NotificationCatalogEntry, NotificationParameters, NotificationProduct, NotificationSchedule
} from './notification-catalog-entry.model';
import { NotificationDelivery } from './notification-delivery.model';
import { NotificationEventSettings } from './notification-event-settings.model';

export interface WireSchedule {
  days?: string[];
  every_hours?: number;
  mode: string;
  month_day?: number;
  times?: string[];
}

export interface WireProduct {
  blurb?: string;
  icon?: string;
  in_plan?: boolean;
  key: string;
  label: string;
  plan_state?: string;
  tint?: string;
}

export interface WireCatalogEntry {
  allows_immediate: boolean;
  allows_periodic: boolean;
  class: string;
  description: string;
  event_type: string;
  has_look_ahead?: boolean;
  has_severity: boolean;
  has_threshold?: boolean;
  label: string;
  planned?: string;
  product: string;
}

export interface WireCatalog {
  events: WireCatalogEntry[];
  products: WireProduct[];
}

export interface WireParameters {
  interval_hours?: number[];
  look_ahead_days?: number[];
  max_times_per_day?: number;
  min_counts?: number[];
  month_days?: number[];
  schedule_modes?: string[];
  severities?: string[];
  weekdays?: string[];
}

export interface WireEventSettings {
  channels: string[];
  emails: string[];
  event_type: string;
  look_ahead_days?: number;
  min_count?: number;
  min_severity: string;
  schedule?: WireSchedule;
  source?: string;
  timezone: string;
}

export interface WireDelivery {
  body_json?: string;
  channel: string;
  created_at: string;
  event_id?: string;
  event_type: string;
  id: string;
  read_at?: string;
  report_type?: string;
  retried_at?: string;
  severity: string;
  status: string;
  subject?: string;
  window_from?: string;
  window_to?: string;
}

const HAS_ZONE: RegExp = /(?:Z|[+-]\d{2}:?\d{2})$/i;
const HAS_TIME: RegExp = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;

/**
 * Parses an API timestamp as an INSTANT, defaulting to UTC when the string names no zone.
 *
 * These columns live in MySQL `datetime`, which carries no zone, so EF Core hands the API a
 * DateTime with `Kind = Unspecified` and System.Text.Json writes it with no trailing `Z` (there is
 * no DateTime converter registered in the API's Startup). `new Date("2026-09-08T20:51:45.594118")`
 * then parses it as LOCAL time, which puts every row hours in the FUTURE for anyone west of UTC —
 * and a negative age falls into `relativeTime`'s `diffMin < 1` branch, so every row in the bell and
 * the feed read "just now" no matter how old it was.
 *
 * The `Z` is appended only when the value has a time component and no zone designator, so this
 * stays correct — not double-shifted — if the API starts emitting a proper instant. A date-only
 * string is left alone: ECMAScript already parses that form as UTC.
 */
function parseUtc(v: string): Date {
  return new Date(HAS_TIME.test(v) && !HAS_ZONE.test(v) ? `${v.replace(' ', 'T')}Z` : v);
}

function toDate(v?: string): Date | undefined {
  return v ? parseUtc(v) : undefined;
}

/**
 * A missing schedule means a document written before the schedule object existed; the API already
 * resolves those to a real schedule, so this only guards a partial payload.
 */
function scheduleFromJson(json?: WireSchedule): NotificationSchedule {
  const sched: NotificationSchedule = new NotificationSchedule();
  if (!json) {
    return sched;
  }
  sched.days = json.days ?? sched.days;
  sched.everyHours = json.every_hours ?? sched.everyHours;
  sched.mode = json.mode as NotificationSchedule['mode'];
  sched.monthDay = json.month_day ?? sched.monthDay;
  sched.times = json.times ?? sched.times;
  return sched;
}

function scheduleToJson(sched: NotificationSchedule): WireSchedule {
  return {
    days: sched.days, every_hours: sched.everyHours, mode: sched.mode, month_day: sched.monthDay, times: sched.times
  };
}

export class NotificationCatalogEntryMapper {
  public fromJson(json: WireCatalogEntry): NotificationCatalogEntry {
    const e: NotificationCatalogEntry = new NotificationCatalogEntry();
    e.allowsImmediate = json.allows_immediate;
    e.allowsPeriodic = json.allows_periodic;
    e.class = json.class as NotificationCatalogEntry['class'];
    e.description = json.description;
    e.eventType = json.event_type;
    e.hasLookAhead = json.has_look_ahead ?? false;
    e.hasSeverity = json.has_severity;
    e.hasThreshold = json.has_threshold ?? false;
    e.label = json.label;
    e.planned = json.planned ?? undefined;
    e.product = json.product;
    return e;
  }

  public toJson(e: NotificationCatalogEntry): WireCatalogEntry {
    return {
      allows_immediate: e.allowsImmediate,
      allows_periodic: e.allowsPeriodic,
      class: e.class,
      description: e.description,
      event_type: e.eventType,
      has_look_ahead: e.hasLookAhead,
      has_severity: e.hasSeverity,
      has_threshold: e.hasThreshold,
      label: e.label,
      planned: e.planned,
      product: e.product
    };
  }
}

export class NotificationProductMapper {
  public fromJson(json: WireProduct): NotificationProduct {
    const pr: NotificationProduct = new NotificationProduct();
    pr.blurb = json.blurb ?? '';
    pr.icon = json.icon ?? 'bell';
    // a product the API did not flag is treated as locked: never assume access the server did not grant
    pr.inPlan = json.in_plan === true;
    pr.key = json.key;
    pr.label = json.label;
    pr.planState = json.plan_state ?? undefined;
    pr.tint = json.tint ?? '#8c1d18';
    return pr;
  }

  public toJson(pr: NotificationProduct): WireProduct {
    return {
      blurb: pr.blurb,
      icon: pr.icon,
      in_plan: pr.inPlan,
      key: pr.key,
      label: pr.label,
      plan_state: pr.planState,
      tint: pr.tint
    };
  }
}

export class NotificationCatalogMapper {
  private readonly entries: NotificationCatalogEntryMapper = new NotificationCatalogEntryMapper();
  private readonly products: NotificationProductMapper = new NotificationProductMapper();

  public fromJson(json: WireCatalog): NotificationCatalog {
    const c: NotificationCatalog = new NotificationCatalog();
    c.events = (json.events ?? []).map(
      (e: WireCatalogEntry): NotificationCatalogEntry => this.entries.fromJson(e)
    );
    c.products = (json.products ?? []).map(
      (pr: WireProduct): NotificationProduct => this.products.fromJson(pr)
    );
    return c;
  }

  public toJson(c: NotificationCatalog): WireCatalog {
    return {
      events: c.events.map((e: NotificationCatalogEntry): WireCatalogEntry => this.entries.toJson(e)),
      products: c.products.map((pr: NotificationProduct): WireProduct => this.products.toJson(pr))
    };
  }
}

export class NotificationParametersMapper {
  public fromJson(json: WireParameters): NotificationParameters {
    // Every field comes from the API or stays empty. No local defaults (Codex 4.1).
    const pa: NotificationParameters = new NotificationParameters();
    pa.intervalHours = json.interval_hours ?? [];
    pa.lookAheadDays = json.look_ahead_days ?? [];
    pa.maxTimesPerDay = json.max_times_per_day ?? 0;
    pa.minCounts = json.min_counts ?? [];
    pa.monthDays = json.month_days ?? [];
    pa.scheduleModes = (json.schedule_modes ?? []) as NotificationParameters['scheduleModes'];
    pa.severities = (json.severities ?? []) as NotificationParameters['severities'];
    pa.weekdays = json.weekdays ?? [];
    return pa;
  }

  public toJson(pa: NotificationParameters): WireParameters {
    return {
      interval_hours: pa.intervalHours,
      look_ahead_days: pa.lookAheadDays,
      max_times_per_day: pa.maxTimesPerDay,
      min_counts: pa.minCounts,
      month_days: pa.monthDays,
      schedule_modes: pa.scheduleModes,
      severities: pa.severities,
      weekdays: pa.weekdays
    };
  }
}

/** Maps GET/PUT /v1/notifications/settings rows. `channels` carries `email`/`in_app` only. */
export class NotificationEventSettingsMapper {
  public fromJson(json: WireEventSettings): NotificationEventSettings {
    return {
      // The wire payload is untyped JSON (string[]); the API is the authority that only ever sends
      // 'email'/'in_app' here (channels is validated server-side, and the webhook value is rejected
      // outright), so this cast is the one place that boundary is crossed.
      channels: (json.channels ?? []) as NotificationEventSettings['channels'],
      emails: json.emails ?? [],
      eventType: json.event_type,
      lookAheadDays: json.look_ahead_days ?? undefined,
      minCount: json.min_count ?? undefined,
      minSeverity: json.min_severity as NotificationEventSettings['minSeverity'],
      schedule: scheduleFromJson(json.schedule),
      source: json.source ?? 'admin',
      timezone: json.timezone ?? 'UTC'
    };
  }

  public toJson(s: NotificationEventSettings): WireEventSettings {
    return {
      channels: s.channels,
      emails: s.emails,
      event_type: s.eventType,
      look_ahead_days: s.lookAheadDays,
      min_count: s.minCount,
      min_severity: s.minSeverity,
      schedule: scheduleToJson(s.schedule),
      source: s.source,
      timezone: s.timezone
    };
  }
}

export class NotificationDeliverySerializer implements Serializer {
  public fromJson(json: WireDelivery): NotificationDelivery {
    const d: NotificationDelivery = new NotificationDelivery();
    d.id = json.id;
    d.bodyJson = json.body_json ?? '{}';
    d.channel = json.channel;
    d.createdAt = parseUtc(json.created_at);
    d.eventId = json.event_id ?? undefined;
    d.eventType = json.event_type;
    d.readAt = toDate(json.read_at);
    d.reportType = json.report_type ?? undefined;
    d.retriedAt = toDate(json.retried_at);
    d.severity = json.severity as NotificationDelivery['severity'];
    d.status = json.status as NotificationDelivery['status'];
    d.subject = json.subject ?? '';
    d.windowFrom = toDate(json.window_from);
    d.windowTo = toDate(json.window_to);
    return d;
  }

  public toJson(d: NotificationDelivery): Partial<WireDelivery> {
    return { id: d.id };
  }
}
